import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $convertToMarkdownString, TRANSFORMERS } from '@lexical/markdown';
import type { Transformer } from '@lexical/markdown';
import { useEffect } from 'react';

interface OnChangeMarkdownProps {
  onChange: (markdown: string) => void;
  /** Optional transformer list. Defaults to TRANSFORMERS when omitted. */
  transformers?: Transformer[];
}

/**
 * Update tag for programmatic content loads (the async `initialValue` reload).
 * Tagged updates are skipped by `onChange` so a fetched value isn't echoed back
 * as if the user typed it. A custom tag (not `history-merge`) is required
 * because Lexical also tags the *initial* mount with `history-merge`, and that
 * first load must still emit through `onChange`.
 */
export const INIT_RELOAD_TAG = 'erp-md-init-reload';

export function OnChangeMarkdown({
  onChange,
  transformers,
}: OnChangeMarkdownProps) {
  const [editor] = useLexicalComposerContext();
  const activeTransformers = transformers ?? TRANSFORMERS;

  useEffect(() => {
    return editor.registerUpdateListener(
      ({ editorState, dirtyElements, dirtyLeaves, tags }) => {
        // Caret moves and selection changes produce no dirty nodes. Skipping
        // them avoids re-serializing the whole document (and firing onChange)
        // on every cursor movement; we only emit when content actually changed.
        if (dirtyElements.size === 0 && dirtyLeaves.size === 0) return;
        // Programmatic content loads (async initialValue reload) are not user
        // edits, so don't echo them back through onChange — the caller already
        // holds that value. The initial mount load is intentionally NOT tagged
        // this way, so it still emits.
        if (tags.has(INIT_RELOAD_TAG)) return;
        editorState.read(() => {
          const markdown = $convertToMarkdownString(activeTransformers);
          onChange(markdown);
        });
      },
    );
  }, [editor, onChange, activeTransformers]);

  return null;
}
